import { html, TemplateResult } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_FIREWALL_ADVANCED_RULES_FORM } from '../../../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../../../utils/translator.helper';
import { styles } from '../view/styles';
import { GzFormFieldWebComponent } from '../../../../../../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { RequiredValidator } from '../../../../../../../../../ui/gzForm/validators/required.validator';
import { FormGroup } from '../../../../../../../../../ui/gzForm/formGroup/form-group';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../../../../ui/gzInput';
import { GzCheckboxWebComponent } from '../../../../../../../../../ui/gzCheckbox';
import { GzPolicyFullPageWindowFormWebComponent } from '../../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/form';
import { GzDropdownWebComponent } from '../../../../../../../../../ui/gzDropdown';
import { GzChipsGeneratorInputWebComponent } from '../../../../../../../../../ui/gzChipsGeneratorInput';
import {
  FirewallAdvancedDefaultRule,
  FirewallAdvancedRuleFormValue,
  FirewallAdvancedRulePermissionType,
  FirewallAdvancedRuleProtocolType,
  FirewallAdvancedRuleSettingsDirection,
  FirewallAdvancedRuleSettingsIpVersion,
  FirewallAdvancedRuleType,
  ModuleId,
  SectionId,
} from '@nirvana/shared/dtos/types/policies/common';
import { RegexValidator } from '../../../../../../../../../ui/gzForm/validators/regex.validator';
import { GzHardCodedFormField } from '../../../../../../../../../ui/gzForm/formField/component/GzHardCodedFormField';
import { getAdvancedProtocolOptionsForDropdown } from '../../../../../../utils/uiSettings/firewall/firewall.values';
import { IpValidator } from '../../../../../../../../../ui/gzForm/validators/ip.validator';
import { PortsMultipleValidator } from '../../../../../../../../../ui/gzForm/validators/ports-multiple.validator';
import { Md5Validator } from '../../../../../../../../../ui/gzForm/validators/md5.validator';
import { FontSizes, FontWeights } from '../../../../../../../../../types/common/types';
import { GzRadioGroupWebComponent } from '../../../../../../../../../ui/gzRadio';
import _ from 'lodash';
import { CustomizableValidator } from '../../../../../../../../../ui/gzForm/validators/customizable.validator';
import { toggleVisibility } from '../../../../../../../../../utils/htmlElement.utils';
import { isBrokenRule } from '../../../utils/validators';
import { v4 as uuidv4 } from 'uuid';
import { classMap } from 'lit/directives/class-map.js';
import { getSupportLinkForPlatform } from '../../../../../../utils/links';
import { validateUniversalAbsoluteFilePathForFirewallAdvancedRules } from '../../../utils/validators';

@customElement(WEB_COMPONENT_POLICY_PANEL_FIREWALL_ADVANCED_RULES_FORM)
export class GzFirewallAdvancedRulesFormWebComponent extends GzPolicyFullPageWindowFormWebComponent<FirewallAdvancedRuleFormValue> {
  // general section
  @query('#firewall-rule-name-form-field')
  private ruleName: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#priority')
  private rulePriority: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;
  @query('#firewall-rule-application-path-field')
  private ruleApplicationPath: GzFormFieldWebComponent<GzInputWebComponent>;
  @query('#firewall-rule-command-line-form-field')
  private ruleCommandLine: GzFormFieldWebComponent<GzInputWebComponent>;
  @query('#firewall-rule-application-md5-form-field')
  private ruleApplicationMd5: GzFormFieldWebComponent<GzInputWebComponent>;

  // settings section
  @query('#firewall-rule-local-address-any-form-field')
  private localAddressAny: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#firewall-rule-local-address-ip-form-field')
  private localAddressIp: GzFormFieldWebComponent<GzInputWebComponent>;
  @query('#firewall-rule-local-address-port-form-field')
  private localAddressPort: GzFormFieldWebComponent<GzInputWebComponent>;

  @query('#firewall-rule-remote-address-any-form-field')
  private remoteAddressAny: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#firewall-rule-remote-address-ip-form-field')
  private remoteAddressIp: GzFormFieldWebComponent<GzInputWebComponent>;
  @query('#firewall-rule-remote-address-port-form-field')
  private remoteAddressPort: GzFormFieldWebComponent<GzInputWebComponent>;

  @query('#directly-connected-checkbox-field')
  private directlyConnectedCheckboxFormField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#directly-connected-remote-mac-field')
  private directlyConnectedRemoteMacFormField: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent>;

  @query('#firewall-rule-protocol-type-form-field')
  private ruleProtocolType: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;
  @query('#firewall-rule-custom-protocol-field')
  private ruleCustomProtocol: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#firewall-rule-direction-form-field')
  private ruleDirection: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;
  @query('#firewall-rule-ip-version-form-field')
  private ruleIpVersion: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;

  // network section
  @query('#firewall-rule-permission-home-form-field')
  private rulePermissionHome: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#firewall-rule-permission-public-form-field')
  private rulePermissionPublic: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#firewall-rule-set-permission-form-field')
  private ruleSetPermission: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;

  private readonly maxPriority: number;
  private readonly ruleType: FirewallAdvancedRuleType;

  public constructor(maxPriority: number, ruleType: FirewallAdvancedRuleType, value?: FirewallAdvancedRuleFormValue) {
    super(value);
    this.maxPriority = maxPriority;
    this.ruleType = ruleType;
  }

  static get styles() {
    return styles;
  }

  public setError(error: string) {
    this.ruleName.setError(error);
  }

  protected getConvertedFormValue() {
    const formValue = this.formGroup.value;

    const isRemoteMacEnabled = _.get(formValue, 'settings.directlyConnected.enable', false);
    if (isRemoteMacEnabled) {
      let remoteMacs = _.get(formValue, 'settings.directlyConnected.remoteMac', []) as string[];

      // remove duplicates and convert to uppercase with colon as separator
      remoteMacs = remoteMacs
        .map(mac => mac.replace(/-/g, ':').toUpperCase())
        .filter((mac, index, self) => index === self.indexOf(mac));

      _.set(formValue, 'settings.directlyConnected.remoteMac', remoteMacs.join(';'));
    }

    return formValue;
  }

  createFormGroup(): void {
    this.ruleName.Validators = [
      RequiredValidator(translateFn('Required field')),
      RegexValidator(/^[^\s+$]/, translateFn('Invalid rule name')),
    ];
    this.ruleApplicationPath.Validators =
      this.ruleType === FirewallAdvancedRuleType.APPLICATION
        ? [
            RequiredValidator(translateFn('Required field')),
            CustomizableValidator(validateUniversalAbsoluteFilePathForFirewallAdvancedRules),
          ]
        : [CustomizableValidator(validateUniversalAbsoluteFilePathForFirewallAdvancedRules)];
    this.ruleApplicationMd5.Validators = [Md5Validator(translateFn('Invalid MD5 hash'))];
    this.localAddressIp.Validators = [
      RequiredValidator(translateFn('Required field')),
      IpValidator(translateFn('Invalid IP address'), true, true),
    ];
    this.localAddressPort.Validators = [PortsMultipleValidator(translateFn('Invalid port or port range'))];
    this.remoteAddressIp.Validators = [
      RequiredValidator(translateFn('Required field')),
      IpValidator(translateFn('Invalid IP address'), true, true),
    ];
    this.remoteAddressPort.Validators = [PortsMultipleValidator(translateFn('Invalid port or port range'))];
    this.ruleCustomProtocol.Validators = [RequiredValidator(translateFn('Required field'))];

    this.rulePermissionHome.touched = true;
    this.rulePermissionPublic.touched = true;
    this.rulePermissionHome.Validators = [
      CustomizableValidator(value => {
        if (this.rulePermissionHome.value || this.rulePermissionPublic.value) {
          return { valid: true };
        }
        return { valid: false, error: translateFn('Select at least one network profile.') };
      }),
    ];
    this.rulePermissionPublic.Validators = [
      CustomizableValidator(value => {
        if (this.rulePermissionHome.value || this.rulePermissionPublic.value) {
          return { valid: true };
        }
        return { valid: false, error: translateFn('Select at least one network profile.') };
      }),
    ];

    this.formGroup = FormGroup.build({
      defaultPositionInGrid: this.rulePriority,
      ruleId: new GzHardCodedFormField<string>(uuidv4()),
      defaultRule: new GzHardCodedFormField<number>(FirewallAdvancedDefaultRule.NO),
      ruleType: new GzHardCodedFormField<number>(this.ruleType),
      type: new GzHardCodedFormField<number>(0),
      enabled: new GzHardCodedFormField<boolean>(true),
      details: {
        name: this.ruleName,
        applictionPath: this.ruleApplicationPath,
        commandLine: this.ruleCommandLine,
        applicationMd5: this.ruleApplicationMd5,
      },
      settings: {
        localAddress: {
          any: this.localAddressAny,
          ipMask: this.localAddressIp,
          portRange: this.localAddressPort,
        },
        remoteAddress: {
          any: this.remoteAddressAny,
          ipMask: this.remoteAddressIp,
          portRange: this.remoteAddressPort,
        },
        directlyConnected: {
          enable: this.directlyConnectedCheckboxFormField,
          remoteMac: this.directlyConnectedRemoteMacFormField,
        },
        protocol: this.ruleProtocolType,
        customProtocol: this.ruleCustomProtocol,
        direction: this.ruleDirection,
        ipVersion: this.ruleIpVersion,
      },
      permission: {
        home: this.rulePermissionHome,
        public: this.rulePermissionPublic,
        setPermission: this.ruleSetPermission,
      },
    });
  }

  setFormGroupValue() {
    // hide fields by rule type
    this.hideFieldsByRuleType();

    // validate fields
    this.rulePermissionHome.validate();
    this.rulePermissionPublic.validate();

    if (!this.value) {
      this.rulePriority.value = this.maxPriority;
      this.ruleName.value = 'my-rule';
      return;
    }

    this.formGroup.value = {
      defaultPositionInGrid: this.value.defaultPositionInGrid,
      ruleId: this.value.ruleId,
      defaultRule: this.value.defaultRule,
      ruleType: this.value.ruleType,
      type: this.value.type,
      enabled: this.value.enabled,
      details: this.value.details,
      settings: {
        localAddress: this.value.settings.localAddress,
        remoteAddress: this.value.settings.remoteAddress,
        directlyConnected: {
          enable: this.value.settings.directlyConnected.enable,
          remoteMac: this.value.settings.directlyConnected.remoteMac
            ? this.value.settings.directlyConnected.remoteMac.split(';')
            : [],
        },
        protocol: this.value.settings.protocol,
        customProtocol: this.value.settings.customProtocol,
        direction: this.value.settings.direction,
        ipVersion: this.value.settings.ipVersion,
      },
      permission: this.value.permission,
    };

    if (isBrokenRule(this.value)) {
      this.ruleApplicationPath.validate(true);
    }

  }

  private hideFieldsByRuleType() {
    const isVisible = this.ruleType !== FirewallAdvancedRuleType.CONNECTION;

    toggleVisibility(this.ruleApplicationPath, isVisible);
    toggleVisibility(this.ruleCommandLine, isVisible);
    toggleVisibility(this.ruleApplicationMd5, isVisible);
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div class="rules-form-container">
        <gz-title fontWeight="500" fontSize="18" class="module-subtitle">${translateFn('Details')}</gz-title>
        ${this.renderDetailsFormFields()}

        <gz-title fontWeight="500" fontSize="18" class="module-subtitle mt-5">${translateFn('Settings')}</gz-title>
        ${this.renderSettingsFormFields()}

        <gz-title fontWeight="500" fontSize="18" class="module-subtitle mt-5">${translateFn('Network')}</gz-title>
        ${this.renderNetworkFormFields()}
      </div>
    `;
  }

  private renderDetailsFormFields(): TemplateResult {
    return html` <div class="rules-form w-full">
      <gz-form-field
        id="firewall-rule-name-form-field"
        class="label-mw-250 mt-2"
        label="${translateFn('Rule name')}"
        required
      >
        <gz-input
          inputWidth="${InputWidthValues.LARGE}"
          gz-form-input
          placeholder="${translateFn('Rule name')}"
        ></gz-input>
        <gz-input-error></gz-input-error>
      </gz-form-field>

      <gz-form-field id="priority" class="label-mw-250 mt-2" label="${translateFn('Set priority')}" required>
        <gz-input-number
          gz-form-input
          inputWidth="${InputWidthValues.SMALL}"
          min="${1}"
          max="${this.maxPriority}"
        ></gz-input-number>
        <gz-input-error></gz-input-error>
      </gz-form-field>

      <gz-form-field
        class="label-mw-250 mt-2"
        id="firewall-rule-application-path-field"
        label="${translateFn('Application path')}"
        required
      >
        <gz-input
          gz-form-input
          inputWidth="${InputWidthValues.LARGE}"
          placeholder="${translateFn('Application path')}"
        ></gz-input>
        <gz-link
          fontSize="${FontSizes.PARAGRAPH_12}"
          href="${getSupportLinkForPlatform({ moduleId: ModuleId.FIREWALL, sectionId: SectionId.FIREWALL_ADVANCED }, [
            'suggestionList',
          ])}"
          >${translateFn('View list of suggestions')}</gz-link
        >
      </gz-form-field>

      <gz-form-field
        class="label-mw-250 mt-2"
        id="firewall-rule-command-line-form-field"
        label="${translateFn('Command line')}"
      >
        <gz-input
          inputWidth="${InputWidthValues.LARGE}"
          gz-form-input
          placeholder="${translateFn('Command line')}"
        ></gz-input>
        <gz-text
          id="firewall-rule-command-line-info-text"
          class="mt-1"
          fontSize=${FontSizes.PARAGRAPH_11}
          fontWeight=${FontWeights.NORMAL}
          >${translateFn('Leave blank if unavailable.')}</gz-text
        >
        <gz-input-error></gz-input-error>
      </gz-form-field>

      <gz-form-field
        class="label-mw-250 mt-2"
        id="firewall-rule-application-md5-form-field"
        label="${translateFn('Application MD5')}"
      >
        <gz-input
          inputWidth="${InputWidthValues.LARGE}"
          gz-form-input
          placeholder="${translateFn('MD5 hash')}"
        ></gz-input>
        <gz-text
          id="firewall-rule-application-md5-info-text"
          class="mt-1"
          fontSize=${FontSizes.PARAGRAPH_11}
          fontWeight=${FontWeights.NORMAL}
          >${translateFn('Leave blank if unavailable.')}</gz-text
        >
        <gz-input-error></gz-input-error>
      </gz-form-field>
    </div>`;
  }

  private renderSettingsFormFields(): TemplateResult {
    return html` <div class="rules-form w-full">
      ${this.renderAddressSection(true)} ${this.renderAddressSection(false)}

      <gz-form-field class="label-mw-250 checkbox-width" id="directly-connected-checkbox-field">
        <gz-checkbox
          label="${translateFn('Apply rule only for directly connected endpoints')}"
          gz-form-input
          @change="${e => (this.directlyConnectedRemoteMacFormField.disabled = !e.detail.checked)}"
        ></gz-checkbox>
      </gz-form-field>
      <gz-form-field
        class="ml-5"
        id="directly-connected-remote-mac-field"
        label="${translateFn('Remote MAC')}"
        disabled
      >
        <gz-chips-generator-input
          gz-form-input
          themeInput
          validationRegex="^([0-9A-Fa-f]{2}([:-]|$)){6}$"
          .strings="${{
            placeholder: translateFn('Use a semicolon (;) to separate multiple MAC addresses.'),
            invalidItemErrorMessage: translateFn('Invalid MAC address'),
            notUniqueErrorMessage: translateFn('The entry already exists.'),
          }}"
        ></gz-chips-generator-input>
      </gz-form-field>

      <gz-labeled-content
        class="mt-5"
        hasFormLabelWidth
        labelFontSize="13"
        labelFontWeight="400"
        hasColon
        label="${translateFn('Protocol')}"
      >
        <gz-form-field id="firewall-rule-protocol-type-form-field">
          <gz-tooltip tooltipText="${translateFn('Select protocol')}">
          <gz-radio-group
            gz-form-input
            .selectedOption="${FirewallAdvancedRuleProtocolType.ANY}"
            @radioSelectionChanged=${e =>
              (this.ruleCustomProtocol.disabled = e.detail.selectedValue !== FirewallAdvancedRuleProtocolType.CUSTOM)}
          >
            <gz-radio
              class="mr-7"
              label="${translateFn('Any')}"
              .value="${FirewallAdvancedRuleProtocolType.ANY}"
            ></gz-radio>
            <gz-radio
              class="mr-7"
              label="${translateFn('TCP')}"
              .value="${FirewallAdvancedRuleProtocolType.TCP}"
            ></gz-radio>
            <gz-radio
              class="mr-7"
              label="${translateFn('UDP')}"
              .value="${FirewallAdvancedRuleProtocolType.UDP}"
            ></gz-radio>
            <div class="radio">
              <gz-radio label="" .value="${FirewallAdvancedRuleProtocolType.CUSTOM}" wrapped> </gz-radio>

              <gz-form-field class="dropdown-field-inside-radio" id="firewall-rule-custom-protocol-field" disabled>
                <gz-dropdown
                  class="small-dropdown-width"
                  gz-form-input
                  options="${JSON.stringify(getAdvancedProtocolOptionsForDropdown())}"
                ></gz-dropdown>
                <gz-input-error></gz-input-error>
              </gz-form-field>
            </div>
          </gz-radio-group>
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </gz-labeled-content>

      <gz-labeled-content
        hasFormLabelWidth
        hasColon
        labelFontSize="13"
        labelFontWeight="400"
        label="${translateFn('Direction')}"
      >
        <gz-form-field id="firewall-rule-direction-form-field">
          <gz-radio-group gz-form-input .selectedOption="${FirewallAdvancedRuleSettingsDirection.BOTH}">
            <gz-radio
              class="mr-7"
              label="${translateFn('Both')}"
              .value="${FirewallAdvancedRuleSettingsDirection.BOTH}"
            ></gz-radio>
            <gz-radio
              class="mr-7"
              label="${translateFn('Inbound')}"
              .value="${FirewallAdvancedRuleSettingsDirection.INBOUND}"
            ></gz-radio>
            <gz-radio
              label="${translateFn('Outbound')}"
              .value="${FirewallAdvancedRuleSettingsDirection.OUTBOUND}"
            ></gz-radio>
          </gz-radio-group>
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </gz-labeled-content>

      <gz-labeled-content
        hasFormLabelWidth
        hasColon
        labelFontSize="13"
        labelFontWeight="400"
        label="${translateFn('IP Version')}"
      >
        <gz-form-field id="firewall-rule-ip-version-form-field">
          <gz-radio-group gz-form-input .selectedOption="${FirewallAdvancedRuleSettingsIpVersion.ANY}">
            <gz-radio
              class="mr-7"
              label="${translateFn('Any')}"
              .value="${FirewallAdvancedRuleSettingsIpVersion.ANY}"
            ></gz-radio>
            <gz-radio
              class="mr-7"
              label="${translateFn('IPv4')}"
              .value="${FirewallAdvancedRuleSettingsIpVersion.IPV4}"
            ></gz-radio>
            <gz-radio label="${translateFn('IPv6')}" .value="${FirewallAdvancedRuleSettingsIpVersion.IPV6}"></gz-radio>
          </gz-radio-group>
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </gz-labeled-content>
    </div>`;
  }

  private renderAddressSection(isLocal: boolean): TemplateResult {
    const idPart = isLocal ? 'local' : 'remote';
    const classes = classMap({
      'mt-2': isLocal,
    });

    return html`
      <gz-text class="${classes}" fontSize=${FontSizes.PARAGRAPH_13} fontWeight=${FontWeights.NORMAL}
        >${isLocal ? translateFn('Local address') : translateFn('Remote address') + ':'}</gz-text
      >
      <div class="flex">
        <gz-form-field
          class="small-label-width custom-mt mr-9"
          id="${'firewall-rule-' + idPart + '-address-any-form-field'}"
        >
          <gz-checkbox
            label="${translateFn('Any')}"
            gz-form-input
            @change="${e => ((isLocal ? this.localAddressIp : this.remoteAddressIp).disabled = e.detail.checked)}"
          ></gz-checkbox>
        </gz-form-field>

        <gz-form-field
          id="${'firewall-rule-' + idPart + '-address-ip-form-field'}"
          class="small-label-width mt-1 mr-9"
          label="${translateFn('IP or IP/mask')}"
          required
        >
          <gz-input
            inputWidth="${InputWidthValues.XLARGE}"
            gz-form-input
            placeholder="${translateFn('IP or IP/mask')}"
          ></gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-form-field
          id="${'firewall-rule-' + idPart + '-address-port-form-field'}"
          class="small-label-width mt-1 mr-9"
          label="${translateFn('Port or port range')}"
        >
          <gz-input
            inputWidth="${InputWidthValues.XLARGE}"
            gz-form-input
            placeholder="${translateFn('Port or port range')}"
          ></gz-input>
          <gz-text class="mt-1" fontSize=${FontSizes.PARAGRAPH_11} fontWeight=${FontWeights.NORMAL}
            >${translateFn('Use a semicolon (;) to separate multiple items.')}</gz-text
          >
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </div>
    `;
  }

  private renderNetworkFormFields(): TemplateResult {
    return html`
      <gz-labeled-content
        hasFormLabelWidth
        hasColon
        labelFontSize="13"
        labelFontWeight="400"
        label="${translateFn('Network')}"
      >
        <gz-form-field id="firewall-rule-permission-home-form-field" manualValidation>
          <gz-checkbox
            label="${translateFn('Home/Office')}"
            gz-form-input
            checked
            @change="${this.validateNetworkFields}"
          ></gz-checkbox>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-form-field id="firewall-rule-permission-public-form-field" manualValidation>
          <gz-checkbox
            label="${translateFn('Public')}"
            gz-form-input
            checked
            @change="${this.validateNetworkFields}"
          ></gz-checkbox>
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </gz-labeled-content>

      <gz-labeled-content
        hasFormLabelWidth
        hasColon
        labelFontSize="13"
        labelFontWeight="400"
        label="${translateFn('Permission')}"
      >
        <gz-form-field id="firewall-rule-set-permission-form-field">
          <gz-radio-group gz-form-input .selectedOption="${FirewallAdvancedRulePermissionType.ALLOW}">
            <gz-radio
              class="mr-7"
              label="${translateFn('Allow')}"
              .value="${FirewallAdvancedRulePermissionType.ALLOW}"
            ></gz-radio>
            <gz-radio label="${translateFn('Deny')}" .value="${FirewallAdvancedRulePermissionType.DENY}"></gz-radio>
          </gz-radio-group>
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </gz-labeled-content>
    `;
  }

  private validateNetworkFields() {
    this.rulePermissionHome.validate();
    this.rulePermissionPublic.validate();
  }
}
